export const OMNICINE_TIMELINE_HELP_OPEN_EVENT = "omnicine:timeline:help:open" as const;
export const OMNICINE_TIMELINE_HELP_OPEN_LEGACY_EVENT = "omnicine.timeline.help.open" as const;
export const OMNICINE_TIMELINE_HELP_TOGGLE_EVENT = "omnicine:timeline:help:toggle" as const;
export const OMNICINE_TIMELINE_HELP_CLOSE_EVENT = "omnicine:timeline:help:close" as const;

export const OMNICINE_ATOMIC_RUNS_OPEN_EVENT = "omnicine:atomicRuns:open" as const;
export const OMNICINE_ATOMIC_RUNS_UPDATE_EVENT = "omnicine:atomicRuns:update" as const;
export const OMNICINE_TRANSPORT_PLAYING_EVENT = "omnicine:transport:playing" as const;
export const OMNICINE_TIMELINE_FOLLOW_PLAYHEAD_SET_EVENT = "omnicine:timeline:followPlayhead:set" as const;
export const OMNICINE_TIMELINE_FOLLOW_PLAYHEAD_TOGGLE_EVENT = "omnicine:timeline:followPlayhead:toggle" as const;
export const OMNICINE_MARKER_CONTEXT_REQUEST_EVENT = "omnicine:marker:context:request" as const;
export const OMNICINE_MARKER_EDIT_REQUEST_EVENT = "omnicine:marker:edit:request" as const;
export const OMNICINE_MARKER_ADDED_EVENT = "omnicine:marker:added" as const;
export const OMNICINE_VIDEO_ANNOTATE_ADD_MARKER_EVENT = "omnicine:video:annotate:addMarker" as const;
export const OMNICINE_ADD_PHASE_RANGES_EVENT = "omnicine:addPhaseRanges" as const;
export const OMNICINE_ADD_PHASE_RANGES_LEGACY_EVENT = "omnicine:transport:addPhaseRanges" as const;

export type TimelineHelpOpenDetail = {
  firstRun?: boolean;
  key?: string;
  focus?: string;
};

export type AtomicRunsOpenDetail = {
  sessionId?: string;
  runId?: string;
  startSec?: number;
  durSec?: number;
  source?: string;
};

export type AtomicRunsUpdateDetail = {
  sessionId?: string;
  runs?: unknown[];
  source?: unknown;
  note?: unknown;
};

export type TransportPlayingDetail = {
  enabled?: unknown;
};

export type FollowPlayheadSetDetail = {
  enabled?: unknown;
};

export type MarkerRequestDetail = {
  markerId?: string;
  timeSec?: number;
  label?: string | null;
  type?: string | null;
  clientX?: number;
  clientY?: number;
  source?: string;
};

export type MarkerAddedDetail = Record<string, unknown> & {
  id?: string;
  timeSec?: number;
  label?: string;
  source?: string;
  origin?: string;
};

export type VideoAnnotateAddMarkerDetail = {
  mediaType?: unknown;
  focusScope?: unknown;
  title?: unknown;
  timeSec?: unknown;
  currentTimeSec?: unknown;
  durationSec?: unknown;
  label?: unknown;
  clipLabel?: unknown;
  sourceId?: unknown;
  sourceLabel?: unknown;
  sequenceLabel?: unknown;
  mediaUrl?: unknown;
  mediaFamilyLabel?: unknown;
  status?: unknown;
  annotationType?: unknown;
  annotationSubtype?: unknown;
  annotationSeverity?: unknown;
  annotationSummary?: unknown;
  annotationSource?: unknown;
  annotationId?: unknown;
  nextAction?: unknown;
  markerVisible?: unknown;
  annotationProvenance?: unknown;
  assetLabel?: unknown;
  clipId?: unknown;
  linkedAudio?: unknown;
  frameIndex?: unknown;
  anchorTimecode?: unknown;
  createdAt?: unknown;
  annotationState?: unknown;
  intent?: unknown;
};

export type AddPhaseRangesDetail = undefined;

type WindowEventHandler<TDetail> = (detail: TDetail, event: CustomEvent<TDetail>) => void;

function emitWindowEvent<TDetail>(eventName: string, detail?: TDetail): void {
  window.dispatchEvent(new CustomEvent<TDetail>(eventName, { detail }));
}

function subscribeWindowEvent<TDetail>(eventName: string, handler: WindowEventHandler<TDetail>): () => void {
  const listener: EventListener = (event) => {
    const customEvent = event as CustomEvent<TDetail>;
    handler(customEvent.detail, customEvent);
  };

  window.addEventListener(eventName, listener);
  return () => window.removeEventListener(eventName, listener);
}

export function emitTimelineHelpOpen(detail?: TimelineHelpOpenDetail): void {
  emitWindowEvent(OMNICINE_TIMELINE_HELP_OPEN_EVENT, detail);
  emitWindowEvent(OMNICINE_TIMELINE_HELP_OPEN_LEGACY_EVENT, detail);
}

export function onTimelineHelpOpen(handler: WindowEventHandler<TimelineHelpOpenDetail>): () => void {
  const offCurrent = subscribeWindowEvent(OMNICINE_TIMELINE_HELP_OPEN_EVENT, handler);
  const offLegacy = subscribeWindowEvent(OMNICINE_TIMELINE_HELP_OPEN_LEGACY_EVENT, handler);
  return () => {
    offCurrent();
    offLegacy();
  };
}

export function onTimelineHelpToggle(handler: WindowEventHandler<undefined>): () => void {
  return subscribeWindowEvent(OMNICINE_TIMELINE_HELP_TOGGLE_EVENT, handler);
}

export function onTimelineHelpClose(handler: WindowEventHandler<undefined>): () => void {
  return subscribeWindowEvent(OMNICINE_TIMELINE_HELP_CLOSE_EVENT, handler);
}

export function emitTransportPlaying(detail: TransportPlayingDetail): void {
  emitWindowEvent(OMNICINE_TRANSPORT_PLAYING_EVENT, detail);
}

export function onTransportPlaying(handler: WindowEventHandler<TransportPlayingDetail>): () => void {
  return subscribeWindowEvent(OMNICINE_TRANSPORT_PLAYING_EVENT, handler);
}

export function onFollowPlayheadSet(handler: WindowEventHandler<FollowPlayheadSetDetail>): () => void {
  return subscribeWindowEvent(OMNICINE_TIMELINE_FOLLOW_PLAYHEAD_SET_EVENT, handler);
}

export function onFollowPlayheadToggle(handler: WindowEventHandler<undefined>): () => void {
  return subscribeWindowEvent(OMNICINE_TIMELINE_FOLLOW_PLAYHEAD_TOGGLE_EVENT, handler);
}

export function emitAtomicRunsOpen(detail: AtomicRunsOpenDetail): void {
  emitWindowEvent(OMNICINE_ATOMIC_RUNS_OPEN_EVENT, detail);
}

export function onAtomicRunsOpen(handler: WindowEventHandler<AtomicRunsOpenDetail>): () => void {
  return subscribeWindowEvent(OMNICINE_ATOMIC_RUNS_OPEN_EVENT, handler);
}

export function emitAtomicRunsUpdate(detail: AtomicRunsUpdateDetail): void {
  emitWindowEvent(OMNICINE_ATOMIC_RUNS_UPDATE_EVENT, detail);
}

export function onAtomicRunsUpdate(handler: WindowEventHandler<AtomicRunsUpdateDetail>): () => void {
  return subscribeWindowEvent(OMNICINE_ATOMIC_RUNS_UPDATE_EVENT, handler);
}

export function emitMarkerContextRequest(detail: MarkerRequestDetail): void {
  emitWindowEvent(OMNICINE_MARKER_CONTEXT_REQUEST_EVENT, detail);
}

export function onMarkerContextRequest(handler: WindowEventHandler<MarkerRequestDetail>): () => void {
  return subscribeWindowEvent(OMNICINE_MARKER_CONTEXT_REQUEST_EVENT, handler);
}

export function emitMarkerEditRequest(detail: MarkerRequestDetail): void {
  emitWindowEvent(OMNICINE_MARKER_EDIT_REQUEST_EVENT, detail);
}

export function onMarkerEditRequest(handler: WindowEventHandler<MarkerRequestDetail>): () => void {
  return subscribeWindowEvent(OMNICINE_MARKER_EDIT_REQUEST_EVENT, handler);
}

export function emitMarkerAdded(detail: MarkerAddedDetail): void {
  emitWindowEvent(OMNICINE_MARKER_ADDED_EVENT, detail);
}

export function emitVideoAnnotateAddMarker(detail: VideoAnnotateAddMarkerDetail): void {
  emitWindowEvent(OMNICINE_VIDEO_ANNOTATE_ADD_MARKER_EVENT, detail);
}

export function onVideoAnnotateAddMarker(handler: WindowEventHandler<VideoAnnotateAddMarkerDetail>): () => void {
  return subscribeWindowEvent(OMNICINE_VIDEO_ANNOTATE_ADD_MARKER_EVENT, handler);
}

export function emitAddPhaseRanges(): void {
  emitWindowEvent<AddPhaseRangesDetail>(OMNICINE_ADD_PHASE_RANGES_EVENT);
  emitWindowEvent<AddPhaseRangesDetail>(OMNICINE_ADD_PHASE_RANGES_LEGACY_EVENT);
}

export function onAddPhaseRanges(handler: WindowEventHandler<AddPhaseRangesDetail>): () => void {
  const offCurrent = subscribeWindowEvent(OMNICINE_ADD_PHASE_RANGES_EVENT, handler);
  const offLegacy = subscribeWindowEvent(OMNICINE_ADD_PHASE_RANGES_LEGACY_EVENT, handler);
  return () => {
    offCurrent();
    offLegacy();
  };
}